import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { 
  Users, 
  FileText, 
  CheckSquare, 
  TrendingUp,
  ChevronRight,
  ArrowLeft,
  X,
  BookOpen,
  AlertCircle,
  User,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  Check,
  Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import RequirementsPanel from '../components/RequirementsPanel';
import StudentAvatar from '../components/StudentAvatar';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  
  // Journal View States
  const [viewingJournal, setViewingJournal] = useState(false);
  const [allClasses, setAllClasses] = useState([]);
  const [selectedJournalClass, setSelectedJournalClass] = useState(null);
  const [classLessons, setClassLessons] = useState([]);
  const [fetchingJournalData, setFetchingJournalData] = useState(false);
  
  // Custom Redesigned States for Journal CRUD / Search (matching Notification page)
  const [journalSearchQuery, setJournalSearchQuery] = useState('');
  const [showJournalFormModal, setShowJournalFormModal] = useState(false);
  const [showJournalDetailModal, setShowJournalDetailModal] = useState(false);
  const [selectedJournalLesson, setSelectedJournalLesson] = useState(null);
  const [editingJournalId, setEditingJournalId] = useState(null);
  const [journalFormData, setJournalFormData] = useState({
    lesson_date: new Date().toISOString().split('T')[0],
    content: ''
  });
  const [savingJournalLesson, setSavingJournalLesson] = useState(false);
  


  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/dashboard/stats');
        setStats(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleJournalClick = async () => {
    setViewingJournal(true);
    setFetchingJournalData(true);
    try {
      const res = await api.get('/classes');
      setAllClasses(res.data);
    } catch (err) {
      console.error('Error fetching classes for journal:', err);
    } finally {
      setFetchingJournalData(false);
    }
  };

  const fetchJournalLessons = async (classId) => {
    try {
      const res = await api.get(`/classes/${classId}/lessons`);
      setClassLessons(res.data);
    } catch (err) {
      console.error('Error fetching lessons:', err);
    }
  };

  const handleClassJournalClick = async (cls) => {
    setSelectedJournalClass(cls);
    setFetchingJournalData(true);
    try {
      await fetchJournalLessons(cls.id);
    } catch (err) {
      console.error('Error fetching lessons:', err);
    } finally {
      setFetchingJournalData(false);
    }
  };

  const handleJournalSave = async (e) => {
    e.preventDefault();
    if (!journalFormData.content) return;
    setSavingJournalLesson(true);
    try {
      if (editingJournalId) {
        await api.put(`/classes/${selectedJournalClass.id}/lessons/${editingJournalId}`, {
          lesson_date: journalFormData.lesson_date,
          content: journalFormData.content
        });
      } else {
        await api.post(`/classes/${selectedJournalClass.id}/lessons`, {
          lesson_date: journalFormData.lesson_date,
          content: journalFormData.content
        });
      }
      setShowJournalFormModal(false);
      fetchJournalLessons(selectedJournalClass.id);
    } catch (err) {
      console.error('Error saving lesson log:', err);
      alert('Error saving lesson log');
    } finally {
      setSavingJournalLesson(false);
    }
  };

  const handleJournalDelete = async (lessonId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this lesson log?')) return;
    try {
      await api.delete(`/classes/${selectedJournalClass.id}/lessons/${lessonId}`);
      fetchJournalLessons(selectedJournalClass.id);
      if (selectedJournalLesson?.id === lessonId) {
        setShowJournalDetailModal(false);
      }
    } catch (err) {
      console.error('Error deleting lesson log:', err);
      alert('Error deleting lesson log');
    }
  };

  const handleJournalEditClick = (lesson, e) => {
    e.stopPropagation();
    setEditingJournalId(lesson.id);
    const formattedDate = new Date(lesson.lesson_date).toISOString().split('T')[0];
    setJournalFormData({
      lesson_date: formattedDate,
      content: lesson.content
    });
    setShowJournalFormModal(true);
  };

  const handleJournalAddClick = () => {
    setEditingJournalId(null);
    setJournalFormData({
      lesson_date: new Date().toISOString().split('T')[0],
      content: ''
    });
    setShowJournalFormModal(true);
  };

  const handleJournalRowClick = (lesson) => {
    setSelectedJournalLesson(lesson);
    setShowJournalDetailModal(true);
  };



  const filteredJournalLessons = useMemo(() => {
    return classLessons.filter(lesson => {
      const query = journalSearchQuery.toLowerCase();
      const dateStr = new Date(lesson.lesson_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toLowerCase();
      const content = lesson.content.toLowerCase();
      const creator = (lesson.creator_name || '').toLowerCase();
      return dateStr.includes(query) || content.includes(query) || creator.includes(query);
    });
  }, [classLessons, journalSearchQuery]);



  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-bold animate-pulse">Loading Dashboard...</p>
      </div>
    </div>
  );

  if (!stats) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center space-y-4">
        <AlertCircle size={48} className="mx-auto text-rose-500" />
        <p className="text-gray-900 font-bold text-xl">Failed to load dashboard data</p>
        <p className="text-gray-500 text-sm">There was an error connecting to the server.</p>
      </div>
    </div>
  );


  const kpiData = [
    { title: 'Total Students', value: stats.totalStudents, icon: Users, color: 'indigo', trend: '+12% this month', path: '/students' },
    { title: 'Total Applications', value: stats.totalApplications, icon: FileText, color: 'emerald', trend: '+5 new today', path: '/applications' },
    { title: 'Class Journal', value: 'Journal', icon: BookOpen, color: 'purple', trend: 'Daily class logs', action: 'journal' },
    { title: 'IELTS Bookings', value: stats.totalIELTSBookings, icon: BookOpen, color: 'purple', trend: 'Active test prep', path: '/ielts' },
    { title: 'Total Revenue', value: `NRs ${stats.totalRevenue.toLocaleString()}`, icon: TrendingUp, color: 'amber', trend: '+NRs 45k weekly', path: '/payments', adminOnly: true },
    { title: 'Due Payment', value: `NRs ${stats.totalDue.toLocaleString()}`, icon: AlertCircle, color: 'rose', trend: 'Requires follow-up', path: '/payments?filter=due' },
    { title: 'Unpaid Students', value: stats.unpaidCount, icon: AlertCircle, color: 'red', trend: 'Critical attention', path: '/payments?filter=unpaid' },
    { title: 'Pending Tasks', value: stats.pendingTasks, icon: CheckSquare, color: 'rose', trend: '2 high priority', path: '/tasks' },
  ];

  if (viewingJournal) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500 max-w-[1400px] mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => {
                if (selectedJournalClass) setSelectedJournalClass(null);
                else setViewingJournal(false);
              }} 
              className="p-3 bg-white border border-gray-100 hover:bg-gray-50 rounded-2xl shadow-sm transition-all text-gray-600"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h2 className="text-3xl font-black text-gray-900 tracking-tight">
                {selectedJournalClass ? `Class Logs: ${selectedJournalClass.name}` : 'Class Logs'}
              </h2>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mt-1">
                {selectedJournalClass ? 'Lesson history' : 'Select a class'}
              </p>
            </div>
          </div>
          {selectedJournalClass && (
            <button 
              onClick={handleJournalAddClick}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-indigo-100 self-start sm:self-auto"
            >
              <Plus size={16} /> Add Log
            </button>
          )}
        </div>

        {fetchingJournalData ? (
          <div className="py-20 text-center space-y-4">
             <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
             <p className="text-gray-400 font-bold uppercase tracking-widest text-xs animate-pulse">Loading records...</p>
          </div>
        ) : !selectedJournalClass ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allClasses.map(cls => (
              <button 
                key={cls.id} 
                onClick={() => handleClassJournalClick(cls)}
                className="bg-white p-8 rounded-[2.5rem] border border-gray-100 hover:border-indigo-500 transition-all duration-300 text-left shadow-xl shadow-indigo-900/5 group"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300 shadow-sm">
                    <BookOpen size={28} />
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${cls.status === 'Active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-gray-100 text-gray-500'}`}>
                    {cls.status}
                  </span>
                </div>
                <h3 className="text-xl font-black text-gray-900 group-hover:text-indigo-600 transition-colors">{cls.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                   <User size={12} className="text-gray-400" />
                   <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{cls.teacher_name || 'Staff'}</p>
                </div>
                <div className="mt-8 pt-6 border-t border-gray-50 flex items-center justify-between text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] group-hover:translate-x-1 transition-transform">
                  <span>View Class Logs</span>
                  <ChevronRight size={16} />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Search Input */}
            <div className="relative max-w-md">
              <input 
                type="text" 
                value={journalSearchQuery}
                onChange={e => setJournalSearchQuery(e.target.value)}
                placeholder="Search by date or topics..."
                className="w-full pl-12 pr-6 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-gray-800 placeholder:text-gray-400 transition-all"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>

            {/* Tabular List Dashboard */}
            <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl shadow-indigo-900/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest w-40">Date</th>
                      <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest w-48">Recorded By</th>
                      <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Topics Covered</th>
                      <th className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest w-28 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredJournalLessons.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="py-20 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-300 mb-4">
                              <BookOpen size={28} />
                            </div>
                            <p className="text-gray-400 font-black uppercase tracking-widest text-xs">No class logs found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredJournalLessons.map((lesson) => {
                        return (
                          <tr 
                            key={lesson.id} 
                            onClick={() => handleJournalRowClick(lesson)}
                            className="hover:bg-gray-50/60 transition-colors cursor-pointer group"
                          >
                            {/* Date */}
                            <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-gray-900">
                              {new Date(lesson.lesson_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            {/* Recorded By */}
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-black text-indigo-600 uppercase">
                                  {(lesson.creator_name || 'T').charAt(0)}
                                </div>
                                <span className="text-xs font-bold text-gray-700">{lesson.creator_name || 'Teacher'}</span>
                              </div>
                            </td>
                            {/* Topics Covered */}
                            <td className="px-6 py-5">
                              <div className="text-sm font-medium text-gray-500 line-clamp-2 leading-relaxed max-w-xl">
                                {lesson.content}
                              </div>
                            </td>
                            {/* Actions */}
                            <td className="px-6 py-5 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-2">
                                <button 
                                  onClick={() => handleJournalRowClick(lesson)}
                                  className="p-2 bg-gray-50 hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 rounded-xl transition-colors border border-gray-100 hover:border-indigo-100 shadow-sm"
                                  title="View Log Details"
                                >
                                  <Eye size={15} />
                                </button>
                                <button 
                                  onClick={(e) => handleJournalEditClick(lesson, e)}
                                  className="p-2 bg-gray-50 hover:bg-amber-50 text-gray-400 hover:text-amber-600 rounded-xl transition-colors border border-gray-100 hover:border-amber-100 shadow-sm"
                                  title="Edit Log"
                                >
                                  <Edit2 size={15} />
                                </button>
                                <button 
                                  onClick={(e) => handleJournalDelete(lesson.id, e)}
                                  className="p-2 bg-gray-50 hover:bg-rose-50 text-gray-400 hover:text-rose-600 rounded-xl transition-colors border border-gray-100 hover:border-rose-100 shadow-sm"
                                  title="Delete Log"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        <AnimatePresence>
          {showJournalFormModal && (
            <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
              <motion.div 
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl max-w-xl w-full p-8 md:p-10 relative overflow-hidden"
              >
                <button 
                  onClick={() => setShowJournalFormModal(false)}
                  className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-rose-600 shadow-sm border border-gray-100/50 z-10 transition-all"
                >
                  <X size={20} />
                </button>

                <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                  <div className="w-2.5 h-6 bg-indigo-600 rounded-full" />
                  {editingJournalId ? 'Modify Class Log' : 'Add Class Log'}
                </h3>

                <form onSubmit={handleJournalSave} className="space-y-6">
                  <div>
                    <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Date</label>
                    <input 
                      type="date" 
                      value={journalFormData.lesson_date} 
                      onChange={e => setJournalFormData({...journalFormData, lesson_date: e.target.value})} 
                      className="w-full px-6 py-4 rounded-2xl bg-gray-50 focus:bg-white border border-transparent focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 font-bold text-gray-800 placeholder:text-gray-300 transition-all"
                      required 
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">What was taught?</label>
                    <textarea 
                      value={journalFormData.content} 
                      onChange={e => setJournalFormData({...journalFormData, content: e.target.value})} 
                      className="w-full px-6 py-4 rounded-2xl bg-gray-50 focus:bg-white border border-transparent focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 font-medium text-gray-750 placeholder:text-gray-300 min-h-[150px] transition-all"
                      placeholder="Describe the topics covered today..."
                      required
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <button 
                      type="submit" 
                      disabled={savingJournalLesson}
                      className="flex items-center gap-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-sm shadow-xl shadow-indigo-100 transition-all active:scale-95 disabled:opacity-50"
                    >
                      <Check size={20} /> {editingJournalId ? 'Save Changes' : 'Add Log'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Log Detail View Modal */}
        <AnimatePresence>
          {showJournalDetailModal && selectedJournalLesson && (
            <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
              <motion.div 
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl max-w-2xl w-full overflow-hidden relative flex flex-col max-h-[90vh]"
              >
                <button 
                  onClick={() => setShowJournalDetailModal(false)}
                  className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-rose-600 shadow-sm border border-gray-100/50 z-10 transition-all"
                >
                  <X size={20} />
                </button>

                <div className="p-8 md:p-10 border-b border-gray-50 bg-indigo-50/20 relative">
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-wider border border-indigo-100/50 mb-4 inline-block">
                    {selectedJournalClass?.name}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-black text-gray-955 tracking-tight leading-tight mb-4 pr-10">
                    Class Log Details
                  </h3>

                  <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="text-indigo-500" size={14} />
                      {new Date(selectedJournalLesson.lesson_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User className="text-indigo-500" size={14} />
                      By {selectedJournalLesson.creator_name || 'Teacher'}
                    </div>
                  </div>
                </div>

                <div className="p-8 md:p-10 overflow-y-auto flex-1 space-y-8">
                  <div className="prose prose-indigo max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-medium text-base">
                      {selectedJournalLesson.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ... rest of dashboard (Normal View)
  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, idx) => {
          if (kpi.adminOnly && user?.role !== 'Admin') return null;
          const Icon = kpi.icon;
          const colorMap = {
            indigo: 'from-indigo-600 to-blue-700 shadow-indigo-500/10 hover:shadow-indigo-500/20',
            emerald: 'from-emerald-500 to-teal-600 shadow-emerald-500/10 hover:shadow-emerald-500/20',
            purple: 'from-purple-600 to-indigo-700 shadow-purple-500/10 hover:shadow-purple-500/20',
            amber: 'from-amber-500 to-orange-600 shadow-amber-500/10 hover:shadow-amber-500/20',
            rose: 'from-rose-500 to-red-600 shadow-rose-500/10 hover:shadow-rose-500/20',
            red: 'from-red-600 to-rose-700 shadow-red-500/10 hover:shadow-red-500/20'
          };
          return (
            <button 
              key={idx} 
              onClick={() => kpi.action === 'journal' ? handleJournalClick() : navigate(kpi.path)}
              className={`bg-gradient-to-br ${colorMap[kpi.color] || 'from-blue-600 to-blue-800'} p-6 rounded-2xl shadow-lg flex flex-col justify-between h-40 relative overflow-hidden group hover:-translate-y-1 hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 text-white text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 focus-visible:outline-none`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/20`}>
                  {Icon && <Icon size={20} />}
                </div>
                <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">{kpi.title}</span>
              </div>
              <h4 className="text-3xl font-black tracking-tighter">{kpi.value}</h4>
              <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest opacity-70">
                 <TrendingUp size={12} />
                 <span>{kpi.trend}</span>
              </div>
            </button>
          );
        })}
      </div>

      {user?.role === 'Admin' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl shadow-indigo-900/5">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-black text-gray-900 flex items-center gap-3">
                  <div className="w-1.5 h-6 bg-indigo-600 rounded-full" />
                  Recent Applications
                </h2>
                <button onClick={() => navigate('/applications')} className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:translate-x-1 transition-transform flex items-center gap-1">View All <ChevronRight size={14} /></button>
              </div>
              <div className="space-y-4">
                {stats.recentApplications?.map(app => (
                  <div key={app.id} className="flex items-center justify-between p-5 bg-gray-50/50 hover:bg-white border border-transparent hover:border-indigo-100 rounded-2xl transition-all group">
                    <div className="flex items-center gap-4">
                      <StudentAvatar photoUrl={app.photo_url} fullName={app.student_name} className="w-12 h-12 rounded-xl" />
                      <div>
                        <p className="font-black text-gray-900 group-hover:text-indigo-600 transition-colors">{app.application_name}</p>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{app.student_name}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${app.status === 'Accepted' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                      {app.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl shadow-indigo-900/5">
            <h2 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
              Status Breakdown
            </h2>
            <div className="space-y-6">
               {stats.applicationsBreakdown?.map((item, idx) => (
                  <div key={idx} className="group">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{item.status}</span>
                      <span className="text-xs font-black text-gray-900">{item.count}</span>
                    </div>
                    <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${(item.count / stats.totalApplications) * 100}%` }}
                        className={`h-full rounded-full ${
                          item.status === 'Accepted' ? 'bg-emerald-500' : item.status === 'Pending' ? 'bg-amber-500' : 'bg-indigo-500'
                        }`}
                      />
                    </div>
                  </div>
               ))}
            </div>
            <div className="mt-12 p-6 bg-indigo-900 rounded-[2rem] text-white">
               <p className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-2">Helpful Tip</p>
               <p className="text-xs font-medium leading-relaxed">Follow up with <span className="text-amber-400 font-black">{stats.pendingTasks} pending tasks</span> to improve conversion by 15% this week.</p>
            </div>
          </div>
        </div>
      ) : null}
      
      {/* Requirements Panel visible to all, editable by admin */}
      <RequirementsPanel />
    </div>
  );
};

export default Dashboard;
